/**
 * Custom API Error Classes
 * Provides structured error handling for API responses
 */

/**
 * Base API Error class
 * @extends {Error}
 */
export class ApiError extends Error {
  /**
   * @param {string} message - Error message
   * @param {number} status - HTTP status code
   * @param {Object|null} errors - Additional error details
   * @param {Object|null} response - Full response object
   */
  constructor(message, status = 500, errors = null, response = null, code = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errors = errors;
    this.response = response;
    this.code = code;
    this.timestamp = new Date().toISOString();
  }

  /**
   * Check if error is a client error (4xx)
   * @returns {boolean}
   */
  isClientError() {
    return this.status >= 400 && this.status < 500;
  }

  /**
   * Check if error is a server error (5xx)
   * @returns {boolean}
   */
  isServerError() {
    return this.status >= 500;
  }

  /**
   * Check if error is unauthorized (401)
   * @returns {boolean}
   */
  isUnauthorized() {
    return this.status === 401;
  }

  /**
   * Check if error is forbidden (403)
   * @returns {boolean}
   */
  isForbidden() {
    return this.status === 403;
  }

  /**
   * Check if error is not found (404)
   * @returns {boolean}
   */
  isNotFound() {
    return this.status === 404;
  }

  /**
   * Get user-friendly error message
   * @returns {string}
   */
  getUserMessage() {
    const fromDetails = formatValidationErrors(this.errors);
    if (fromDetails) return fromDetails;

    if (this.isForbidden()) {
      if (this.message && !this.message.includes('HTTP 403')) {
        return this.message;
      }
      return 'You do not have permission to perform this action. Please contact your administrator if you need access.';
    }
    if (this.isUnauthorized()) {
      return 'You are not authorized. Please log in again.';
    }
    if (this.isNotFound()) {
      return 'The requested resource was not found.';
    }
    if (this.isServerError()) {
      return 'Server error. Please try again later.';
    }
    return this.message || 'An error occurred. Please try again.';
  }
}

/**
 * Network Error class
 * @extends {Error}
 */
export class NetworkError extends Error {
  /**
   * @param {string} message - Error message
   * @param {Error} originalError - Original error object
   */
  constructor(message = 'Network error: Unable to connect to the server', originalError = null) {
    super(message);
    this.name = 'NetworkError';
    this.originalError = originalError;
    this.timestamp = new Date().toISOString();
  }
}

/**
 * Validation Error class
 * @extends {ApiError}
 */
export class ValidationError extends ApiError {
  /**
   * @param {string} message - Error message
   * @param {Object} errors - Validation errors
   */
  constructor(message = 'Validation failed', errors = {}) {
    super(message, 422, errors);
    this.name = 'ValidationError';
  }
}

/**
 * Turn express-validator style error arrays into a single readable message.
 * @param {unknown} errors
 * @returns {string|null}
 */
export function formatValidationErrors(errors) {
  if (!errors) return null;
  if (Array.isArray(errors)) {
    const parts = errors
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') return item.msg || item.message;
        return null;
      })
      .filter(Boolean);
    return parts.length ? parts.join(' ') : null;
  }
  if (typeof errors === 'object') {
    const parts = Object.values(errors)
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .map((value) => (typeof value === 'string' ? value : value?.msg || value?.message))
      .filter(Boolean);
    return parts.length ? parts.join(' ') : null;
  }
  return null;
}

/**
 * Map express-validator errors to form field keys.
 * @param {unknown} errors
 * @returns {Record<string, string>}
 */
export function mapValidationErrorsToFields(errors) {
  if (!Array.isArray(errors)) return {};

  return errors.reduce((acc, item) => {
    if (!item || typeof item !== 'object') return acc;
    const field = item.path || item.param || item.field;
    const message = item.msg || item.message;
    if (field && message) {
      acc[String(field)] = message;
    }
    return acc;
  }, {});
}

const GENERIC_VALIDATION_RE = /^validation failed$/i;

function resolveApiErrorMessage(data = {}, response = null) {
  const errors = data.errors || null;
  const validationDetails = formatValidationErrors(errors);
  const message = data.error || data.message;

  if (validationDetails) {
    if (!message || GENERIC_VALIDATION_RE.test(String(message).trim())) {
      return validationDetails;
    }
    if (!String(message).includes(validationDetails)) {
      return `${message} ${validationDetails}`.trim();
    }
    return String(message);
  }

  if (response?.status === 403 && !message) {
    return 'You do not have permission to perform this action. Please contact your administrator if you need access.';
  }

  if (!message && response) {
    return `HTTP ${response.status}: ${response.statusText}`;
  }

  return message || 'An error occurred. Please try again.';
}

/**
 * Normalize any thrown/rejected value to a serializable string (safe for Redux).
 * @param {unknown} error
 * @param {string} [fallback]
 * @returns {string}
 */
export function getErrorMessage(error, fallback = 'An error occurred. Please try again.') {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  if (error instanceof Error) {
    const fromDetails = formatValidationErrors(error.errors);
    if (fromDetails) return fromDetails;
    const msg = error.message || error.name;
    if (msg && !GENERIC_VALIDATION_RE.test(String(msg).trim())) return msg;
    return fallback;
  }
  if (typeof error === 'object') {
    const fromDetails = formatValidationErrors(error.errors);
    if (fromDetails) return fromDetails;
    const msg = error.message || error.error;
    if (typeof msg === 'string' && msg && !GENERIC_VALIDATION_RE.test(msg.trim())) return msg;
  }
  return fallback;
}

export function createApiError(response, data = {}) {
  const errors = data.errors || null;
  const message = resolveApiErrorMessage(data, response);
  const code = data.code || null;

  if (response.status === 422 || (response.status === 400 && errors)) {
    return new ValidationError(message, errors);
  }

  return new ApiError(message, response.status, errors, response, code);
}
